import {
  clientsDetailsUrl,
  employeesUrl,
  myOrdersUrl,
  orderBulkConsultantsUrl,
  orderBulkPreviewUrl,
  orderBulkRenewUrl,
  orderBulkUrl,
  orderNotifyCandidatesUrl,
  orderPermissionsUrl,
  orderReportsUrl,
  ordersUrl,
  projectsUrl,
} from 'router/url';
import { fetchWithAuthorization } from 'utils/authorizedFetch';
import {
  ApiResult,
  AssignedProject,
  BulkOrderConsultant,
  BulkOrderPreview,
  BulkOrderRenewRequest,
  BulkOrderRequest,
  BulkOrderResult,
  BulkOrderSharedFields,
  DownloadedFile,
  FinanceControlRow,
  OrderCancelRequest,
  OrderCreateRequest,
  OrderDetails,
  OrderEarlyEndRequest,
  OrderFilters,
  OrderHistoryRow,
  OrderPermissions,
  OrderRejectRequest,
  OrderRenewRequest,
  OrderReportFilters,
  OrderReportFormat,
  OrderReportKind,
  OrderSummary,
  OrderTrip,
  Page,
  SelectOption,
} from './types';

const readResponse = async <T>(response: Response): Promise<ApiResult<T>> => {
  const text = await response.text();
  let data: T | undefined;
  let message: string | undefined;
  let code: string | undefined;
  let orderNumber: string | undefined;

  if (text) {
    try {
      const parsed = JSON.parse(text);
      data = parsed;
      message = parsed?.errorMessage || parsed?.message;
      if (!response.ok) {
        code = parsed?.code || undefined;
        orderNumber = parsed?.orderNumber || undefined;
      }
    } catch {
      message = response.ok ? undefined : response.statusText;
    }
  }

  return {
    ok: response.ok,
    status: response.status,
    data,
    message,
    ...(code ? { code } : {}),
    ...(orderNumber ? { orderNumber } : {}),
  };
};

const safeRequest = async <T>(
  url: string,
  init?: RequestInit,
): Promise<ApiResult<T>> => {
  try {
    const response = await fetchWithAuthorization(url, init);
    return readResponse<T>(response);
  } catch (error) {
    return { ok: false, status: 0, message: (error as Error).message };
  }
};

export const getOrderPermissions = async (): Promise<OrderPermissions> => {
  const result = await safeRequest<{
    canManage: boolean;
    canViewAll: boolean;
    canViewOwn: boolean;
  }>(orderPermissionsUrl);

  return {
    canManage: result.ok && result.data?.canManage === true,
    canViewAll: result.ok && result.data?.canViewAll === true,
    canViewOwn: result.ok && result.data?.canViewOwn === true,
    unavailable: !result.ok,
  };
};

const buildOrderListUrl = (
  baseUrl: string,
  filters: OrderFilters,
  page: number,
  size: number,
) => {
  const { sort, ...criteria } = filters;
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
    sort: sort || 'creationDateTime,desc',
  });

  Object.entries(criteria).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => params.append(key, String(item)));
    } else if (value !== undefined && value !== '') {
      params.set(key, String(value));
    }
  });

  return `${baseUrl}?${params.toString()}`;
};

export const buildOrdersUrl = (
  filters: OrderFilters,
  page: number,
  size: number,
) => buildOrderListUrl(ordersUrl, filters, page, size);

export const buildMyOrdersUrl = (
  filters: OrderFilters,
  page: number,
  size: number,
) => buildOrderListUrl(myOrdersUrl, filters, page, size);

export const fetchOrders = (
  filters: OrderFilters,
  page: number,
  size: number,
) => safeRequest<Page<OrderSummary>>(buildOrdersUrl(filters, page, size));

export const fetchOrder = (orderNumber: string) =>
  safeRequest<OrderDetails>(`${ordersUrl}/${encodeURIComponent(orderNumber)}`);

export const fetchMyOrders = (
  filters: OrderFilters,
  page: number,
  size: number,
) => safeRequest<Page<OrderSummary>>(buildMyOrdersUrl(filters, page, size));

export const fetchMyOrder = (orderNumber: string) =>
  safeRequest<OrderDetails>(
    `${myOrdersUrl}/${encodeURIComponent(orderNumber)}`,
  );

// Projects the signed-in user is assigned to; a pending order shows those of
// its client as the projects its acceptance unlocks for reporting.
export const fetchMyAssignedProjects = () =>
  safeRequest<AssignedProject[]>(`${projectsUrl}?assignedToMe=true`);

// Business trips linked to the order; the consultant reads them through the
// my-orders endpoint.
export const fetchOrderTrips = (orderNumber: string, consultantView = false) =>
  safeRequest<OrderTrip[]>(
    `${consultantView ? myOrdersUrl : ordersUrl}/${encodeURIComponent(
      orderNumber,
    )}/business-trips`,
  );

export const createOrder = (request: OrderCreateRequest) =>
  safeRequest<OrderDetails>(ordersUrl, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(request),
  });

const postOrderAction = <T>(url: string, body?: unknown) =>
  safeRequest<T>(url, {
    method: 'POST',
    ...(body === undefined
      ? {}
      : {
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        }),
  });

export const acceptMyOrder = (orderNumber: string) =>
  postOrderAction<OrderDetails>(
    `${myOrdersUrl}/${encodeURIComponent(orderNumber)}/accept`,
  );

export const rejectMyOrder = (
  orderNumber: string,
  request: OrderRejectRequest,
) =>
  postOrderAction<OrderDetails>(
    `${myOrdersUrl}/${encodeURIComponent(orderNumber)}/reject`,
    request,
  );

export const cancelOrder = (orderNumber: string, request: OrderCancelRequest) =>
  postOrderAction<OrderDetails>(
    `${ordersUrl}/${encodeURIComponent(orderNumber)}/cancel`,
    request,
  );

export const earlyEndOrder = (
  orderNumber: string,
  request: OrderEarlyEndRequest,
) =>
  postOrderAction<OrderDetails>(
    `${ordersUrl}/${encodeURIComponent(orderNumber)}/early-end`,
    request,
  );

export const renewOrder = (orderNumber: string, request: OrderRenewRequest) =>
  postOrderAction<OrderDetails>(
    `${ordersUrl}/${encodeURIComponent(orderNumber)}/renew`,
    request,
  );

type EmployeeName = {
  id: number;
  name: string;
  surname: string;
  username?: string;
  awaitingFirstSignIn?: boolean;
};

// The list of a successful response; an error body is not a list.
const listOf = <T>(result: ApiResult<T[]> | null) =>
  result?.ok && Array.isArray(result.data) ? result.data : [];

const toEmployeeOptions = (employees?: EmployeeName[]): SelectOption[] =>
  (employees || []).map(({ id, name, surname, awaitingFirstSignIn }) => ({
    value: id,
    name: `${name} ${surname}`.trim(),
    ...(awaitingFirstSignIn ? { awaitingFirstSignIn } : {}),
  }));

// Employees and clients for the assignment screens. With activeConsultants
// the active accounts that may get an assignment are loaded too, for the
// consultant pickers of Create and Bulk issue: inactive accounts (change
// request of 24.10, B7) and employment-contract employees (change request of
// 26.10, P2) are left out, except accounts the Entra account sync created that
// have not signed in yet (awaitingFirstSignIn, change request of 28.10). With notifyCandidates the
// people Notify also may name: active accounts that manage or view all
// assignments (D2). Lists not asked for stay empty.
export const fetchOrderReferenceData = async ({
  activeConsultants = false,
  notifyCandidates = false,
}: { activeConsultants?: boolean; notifyCandidates?: boolean } = {}) => {
  const [employees, clients, active, candidates] = await Promise.all([
    safeRequest<EmployeeName[]>(employeesUrl),
    safeRequest<Array<{ id: number; clientName: string; status?: string }>>(
      clientsDetailsUrl,
    ),
    activeConsultants
      ? safeRequest<EmployeeName[]>(
          `${employeesUrl}?${new URLSearchParams({
            activeOnly: 'true',
            orderEligible: 'true',
          }).toString()}`,
        )
      : Promise.resolve(null),
    notifyCandidates
      ? safeRequest<Array<{ id: number; name: string }>>(
          orderNotifyCandidatesUrl,
        )
      : Promise.resolve(null),
  ]);

  const employeeOptions = toEmployeeOptions(employees.data);
  const clientOptions: SelectOption[] = (clients.data || []).map(
    ({ id, clientName }) => ({ value: id, name: clientName }),
  );
  const activeClientOptions: SelectOption[] = (clients.data || [])
    .filter(({ status }) => !status || status === 'ACTIVE')
    .map(({ id, clientName }) => ({ value: id, name: clientName }));
  const employeeIdsByUsername: Record<string, number> = {};
  (employees.data || []).forEach(({ id, username }) => {
    if (username) {
      employeeIdsByUsername[username] = id;
    }
  });

  const employeesOk =
    employees.ok && (!active || active.ok) && (!candidates || candidates.ok);
  return {
    ok: employeesOk && clients.ok,
    employeesOk,
    clientsOk: clients.ok,
    employees: employeeOptions,
    activeEmployees: toEmployeeOptions(listOf(active)),
    notifyCandidates: listOf(candidates).map(({ id, name }) => ({
      value: id,
      name,
    })),
    employeeIdsByUsername,
    clients: clientOptions,
    activeClients: activeClientOptions,
  };
};

const toBulkFormData = (file: File, fields: BulkOrderSharedFields) => {
  const body = new FormData();
  body.append('file', file);
  Object.entries(fields).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => body.append(key, String(item)));
    } else if (value !== undefined && value !== null) {
      body.append(key, String(value));
    }
  });
  return body;
};

export const fetchBulkOrderConsultants = (clientId: number) =>
  safeRequest<BulkOrderConsultant[]>(
    `${orderBulkConsultantsUrl}?${new URLSearchParams({
      clientId: String(clientId),
    }).toString()}`,
  );

export const previewBulkOrders = (request: BulkOrderRequest) =>
  safeRequest<BulkOrderPreview>(orderBulkPreviewUrl, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(request),
  });

export const previewBulkOrdersCsv = (
  file: File,
  fields: BulkOrderSharedFields,
) =>
  safeRequest<BulkOrderPreview>(`${orderBulkPreviewUrl}/csv`, {
    method: 'POST',
    body: toBulkFormData(file, fields),
  });

export const issueBulkOrders = (request: BulkOrderRequest) =>
  safeRequest<BulkOrderResult>(orderBulkUrl, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(request),
  });

export const issueBulkOrdersCsv = (file: File, fields: BulkOrderSharedFields) =>
  safeRequest<BulkOrderResult>(`${orderBulkUrl}/csv`, {
    method: 'POST',
    body: toBulkFormData(file, fields),
  });

export const renewBulkOrders = (request: BulkOrderRenewRequest) =>
  postOrderAction<OrderDetails[]>(orderBulkRenewUrl, request);

// The consultant filter applies to the decision and history reports;
// finance control is filtered by period and client only.
export const buildOrderReportUrl = (
  path: string,
  filters: OrderReportFilters,
) => {
  const params = new URLSearchParams({ from: filters.from, to: filters.to });
  if (filters.clientId) {
    params.set('clientId', String(filters.clientId));
  }
  if (filters.consultantId && !path.startsWith('finance-control')) {
    params.set('consultantId', String(filters.consultantId));
  }
  return `${orderReportsUrl}/${path}?${params.toString()}`;
};

export const fetchOrderDecisions = (filters: OrderReportFilters) =>
  safeRequest<OrderHistoryRow[]>(buildOrderReportUrl('decisions', filters));

export const fetchFinanceControl = (filters: OrderReportFilters) =>
  safeRequest<FinanceControlRow[]>(
    buildOrderReportUrl('finance-control', filters),
  );

const filenameFromHeader = (header: string | null, fallback: string) => {
  const match = header?.match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
};

export const fetchOrderReportFile = async (
  kind: OrderReportKind,
  format: OrderReportFormat,
  filters: OrderReportFilters,
): Promise<ApiResult<DownloadedFile>> => {
  try {
    const response = await fetchWithAuthorization(
      buildOrderReportUrl(`${kind}/${format}`, filters),
    );
    if (!response.ok) {
      const error = await readResponse<never>(response);
      return { ok: false, status: response.status, message: error.message };
    }
    return {
      ok: true,
      status: response.status,
      data: {
        blob: await response.blob(),
        filename: filenameFromHeader(
          response.headers.get('content-disposition'),
          `assignment-${kind}.${format}`,
        ),
      },
    };
  } catch (error) {
    return { ok: false, status: 0, message: (error as Error).message };
  }
};
